import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

// 곧 다운로드할 파일 묶음의 signed URL 을 한 번에 발급한다. backend
// `POST /v1/datasets/{uuid}/files/sign` (`dataset-file-download.md` §3.3).
//
// 전체 다운로드는 이 경로로 서명한다. 목록에 서명을 인라인해 URL 을 미리 모두 받으면 문제가
// 생긴다:
//   - 파일이 수십만 개면 TTL(기본 1시간) 안에 모두 다운로드하지 못한다.
//   - 그러면 큐 뒤쪽 URL 이 모두 만료된다.
//   - 그 뒤로 파일마다 단건 재서명을 하므로, 인라인 서명으로 없애려던 network N+1 이 다시 생긴다.

// 한 요청에 담을 수 있는 파일 수다. 서버 상한과 같은 값이고, 다운로드 창 크기로도 쓴다.
// 값을 늘리면 만료까지의 여유가 줄고, 값을 줄이면 요청 수가 늘어난다.
export const DATASET_FILE_SIGN_MAX = 200;

// uuid 를 파일로 해석할 때 쓰는 종류다. 세 테이블이 각자 uuid 를 발급한다. 그래서 종류가
// 없으면 서버가 조회할 테이블을 정할 수 없다(backend `DatasetFileKind`).
export type DatasetFileKind = "episode" | "meta" | "extra";

export type DatasetFileRef = {
  kind: DatasetFileKind;
  uuid: string;
};

// 서명 결과 한 행이다. 다운로드할 수 없는 파일은 `url` 이 null 이고 `unavailable_reason` 에
// 사유가 있다. 서버는 배치 전체를 실패로 반환하지 않는다. 그래서 호출부는 행마다 확인한다.
export type SignedDatasetFile = {
  kind: DatasetFileKind;
  uuid: string;
  relative_path: string | null;
  url: string | null;
  expires_at: string | null;
  unavailable_reason: string | null;
};

type DatasetFileSignResp = {
  items: SignedDatasetFile[];
};

/**
 * 파일 묶음을 한 번에 서명한다. 응답은 **요청과 같은 길이·순서**다.
 *
 * 404 dataset.not_found 는 dataset 이 없거나 visibility 조건을 충족하지 않는다는 뜻이다.
 * 개별 파일의 실패는 예외가 아니라 행의 `unavailable_reason` 으로 온다.
 */
export async function signDatasetFiles(
  datasetUuid: string,
  files: readonly DatasetFileRef[],
  signal?: AbortSignal,
): Promise<SignedDatasetFile[]> {
  const response = await apiClient.post<DatasetFileSignResp>(
    apiRoutes.dataset.filesSign(datasetUuid),
    { files },
    { signal },
  );
  return response.data.items;
}
